<template>
  <HeaderBanner
    class="w-100"
    :headerBannerImage="useAsset('img/credit-line/cc_data_banner.png')"
    :headerBannerTitle="t('PAYJOY_CARD_VIEWS.COMPLETE_DATA_TITLE')"
    :headerBannerSubtitle="t('PAYJOY_CARD_VIEWS.COMPLETE_DATA_SUBTITLE')"
  />
  <Alert
    v-if="showAlert"
    :message="alertMessage"
    :alertType="alertType"
    :closable="alertClosable"
    :alertIcon="alertIcon"
    @onClose="dismissAlert"
  />
  <div v-if="country === CL_COUNTRY.CO">
    <ColombiaAddressForm
      @onContinueClick="handleContinueClick"
      @onCancelClick="onCancelClick"
      :hideCancelButton="props.hideCancelButton"
      :source="props.source"
      @goBackClick="goBackClick"
    />
  </div>
  <div v-if="country === CL_COUNTRY.MX">
    <MexicoAddressForm
      :preFilledCityValue="preFilledCityValue"
      :hideCancelButton="props.hideCancelButton"
      :customTitle="props.customTitle"
      :customSubmitCta="props.customSubmitCta"
      :source="props.source"
      @onContinueClick="handleContinueClick"
      @onCancelClick="onCancelClick"
      @goBackClick="goBackClick"
    />
  </div>
</template>

<script setup lang="ts">
import Alert from "@/shared-components/common/Alert.vue";
import ColombiaAddressForm from "./forms/ColombiaAddressForm.vue";
import MexicoAddressForm from "./forms/MexicoAddressForm.vue";
import { useGoogleMapsIntegration } from "./composables/useGoogleMapsIntegration";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import { CL_COUNTRY } from "@/apps/credit-line/types/cl_countries";
import { onMounted, ref } from "vue";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import { ICreditCardAddressFormData } from "@/apps/credit-line/types/forms";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import { AndroidInterface } from "@/apps/core-app-onboarding/services/android.service";
import { ICreditCardUserFormData } from "@/apps/credit-line/types/forms";
import HeaderBanner from "@/apps/credit-line/components/HeaderBanner.vue";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { useI18n } from "@/apps/credit-line/composables/useI18n";

const { t } = useI18n();

declare const Android: AndroidInterface;

interface Props {
  preFilledRegionValue?: string;
  preFilledCityValue?: string;
  deviceTag?: string;
  countryDashboard?: string;
  customTitle?: string;
  customSubmitCta?: string;
  hideCancelButton?: boolean;
  preFilledData?: ICreditCardUserFormData | null;
  creditLineId?: number;
  source?: string;
  isGoogleAutocompleteNotEnabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  preFilledRegionValue: "",
  preFilledCityValue: "",
  deviceTag: "",
  customTitle: undefined,
  countryDashboard: "",
  hideCancelButton: false,
  preFilledData: null,
  creditLineId: 0,
  source: "",
  isGoogleAutocompleteNotEnabled: false,
});

const emit = defineEmits<{
  (e: "onContinueClick", data: ICreditCardAddressFormData): void;
  (e: "onCancelClick"): void;
  (e: "goBackClick"): void;
}>();

const {
  showAlert,
  alertType,
  alertMessage,
  alertClosable,
  alertIcon,
  dismissAlert,
} = useGoogleMapsIntegration();

// Map country strings from API to CL_COUNTRY enum values
const mapCountryToCLCountry = (countryString: string): CL_COUNTRY => {
  switch (countryString?.toLowerCase()) {
    case "mexico":
    case "mx":
      return CL_COUNTRY.MX;
    case "colombia":
    case "co":
      return CL_COUNTRY.CO;
    default:
      console.warn(`Unknown country: ${countryString}, defaulting to Mexico`);
      return CL_COUNTRY.MX;
  }
};

// Initialize country based on prop or fallback to Mexico
const country = ref<CL_COUNTRY | null>(
  props.countryDashboard
    ? mapCountryToCLCountry(props.countryDashboard)
    : CL_COUNTRY.MX
);

let shippingFormLoaded = false;
const { fetchSummary } = useCreditLineSummary();

const getSummary = () => {
  fetchSummary().then((summary) => {
    // Only use summary country if no prop country was provided
    if (summary && !props.countryDashboard) {
      country.value = summary?.country;
    }
  });
};

onMounted(async () => {
  if (props.source === "dashboardTestingTool") {
    country.value = props.countryDashboard as CL_COUNTRY;
    return;
  }
  if (!shippingFormLoaded && AnalyticEvents?.CreditLine?.CcShippingFormLoaded) {
    shippingFormLoaded = true;
    logAnalyticsEvent(
      AnalyticEvents.CreditLine.CcShippingFormLoaded,
      "CC_Details"
    );
  }

  // Only call getSummary() if no country prop was provided (i.e., not from dashboard)
  if (!props.countryDashboard) {
    try {
      getSummary();
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  }

  if (isAndroid()) {
    // eslint-disable-next-line no-undef
    Android.finishedLoading();
  }
});

const handleContinueClick = (data) => {
  delete props.preFilledData?.source;
  logAnalyticsEvent(
    AnalyticEvents.CreditLine.CcShippingConfirmDetails,
    "CC_Details",
    undefined,
    {
      ...props.preFilledData,
      data,
    }
  );
  emit("onContinueClick", {
    ...data,
    country: country.value ?? CL_COUNTRY.MX,
  });
};

const onCancelClick = () => {
  emit("onCancelClick");
};

const goBackClick = () => {
  emit("goBackClick");
};
</script>
